import React, { useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Flame } from "lucide-react";

export default function StatsModal({ open, onOpenChange, stats, user }) {
  const played = stats?.played ?? 0;
  const wins = stats?.wins ?? 0;
  const winPct = played ? Math.round((wins * 100) / played) : 0;
  const metrics = [
    { label: "Played", value: played },
    { label: "Win %", value: winPct },
    { label: "Current", value: stats?.current_streak ?? 0 },
    { label: "Best", value: stats?.max_streak ?? 0 },
  ];

  const byDiscipline = useMemo(() => {
    const raw = stats?.by_discipline || {};
    return Object.entries(raw)
      .map(([name, s]) => ({ name, ...s }))
      .sort(
        (a, b) =>
          (b.current_streak || 0) - (a.current_streak || 0) ||
          (b.wins || 0) - (a.wins || 0) ||
          a.name.localeCompare(b.name)
      );
  }, [stats]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="stats-modal"
        className="bg-white border border-slate-200 rounded-2xl w-[calc(100%-1rem)] max-w-lg p-0 overflow-hidden flex flex-col max-h-[92dvh] sm:max-h-[85vh]"
      >
        <div className="shrink-0 px-5 sm:px-6 py-4 sm:py-5">
          <DialogHeader>
            <p className="text-[11px] font-mono tracking-[0.2em] uppercase text-slate-500">
              Your record
            </p>
            <DialogTitle
              className="text-slate-900 text-xl sm:text-2xl font-bold tracking-tight"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Statistics
            </DialogTitle>
          </DialogHeader>
        </div>

        <div className="flex-1 overflow-y-auto px-5 sm:px-6 pb-5 sm:pb-6 space-y-5">
          <div className="grid grid-cols-4 gap-2 sm:gap-3">
            {metrics.map((m) => (
              <div
                key={m.label}
                className="rounded-xl bg-slate-50 border border-slate-200 px-1 sm:px-2 py-3 sm:py-4 text-center"
              >
                <p className="text-2xl sm:text-3xl font-bold text-slate-900 font-mono">
                  {m.value}
                </p>
                <p className="text-[10px] font-mono tracking-[0.1em] sm:tracking-[0.15em] uppercase text-slate-500 mt-1">
                  {m.label}
                </p>
              </div>
            ))}
          </div>

          {user ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-[11px] font-mono tracking-[0.2em] uppercase text-slate-500">
                  By discipline
                </p>
                <p className="text-[10px] text-slate-400">
                  {byDiscipline.length} played
                </p>
              </div>
              {byDiscipline.length === 0 ? (
                <div
                  data-testid="by-discipline-empty"
                  className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-5 text-center text-sm text-slate-500"
                >
                  Play a case in any discipline to start tracking a streak here.
                </div>
              ) : (
                <div className="space-y-1.5" data-testid="by-discipline-list">
                  {byDiscipline.map((d) => (
                    <div
                      key={d.name}
                      data-testid={`by-discipline-row-${d.name}`}
                      className="flex items-center justify-between gap-3 rounded-xl bg-slate-50 border border-slate-200 px-3 py-2.5"
                    >
                      <div className="min-w-0 flex-1">
                        <p
                          className="text-sm font-semibold text-slate-900 truncate"
                          style={{ fontFamily: "Outfit, sans-serif" }}
                        >
                          {d.name}
                        </p>
                        <p className="text-[10px] font-mono tracking-[0.1em] uppercase text-slate-500 mt-0.5">
                          {d.wins}/{d.played} solved · best {d.max_streak}
                        </p>
                      </div>
                      <div
                        className={`shrink-0 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold font-mono ${
                          d.current_streak > 0
                            ? "bg-orange-100 text-orange-700"
                            : "bg-slate-200 text-slate-500"
                        }`}
                      >
                        <Flame className="h-3 w-3" />
                        {d.current_streak}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div
              data-testid="signed-out-hint"
              className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-700 leading-relaxed"
            >
              <p className="font-semibold text-slate-900 mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>
                Sign in for per-discipline streaks
              </p>
              <p className="text-slate-600 text-xs">
                Track a separate streak for each of the 17 disciplines you play, and sync your record across devices.
              </p>
            </div>
          )}

          <p className="text-xs text-slate-500 leading-relaxed">
            A new case drops for every discipline daily at 00:00 UTC.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
